import type { TFunction } from "i18next";
import i18n from "./index";
import { AppLocale, DEFAULT_LOCALE, LOCALES } from "./localesConfig";

const currentLocale = (): AppLocale => {
  const language = i18n.language as AppLocale;
  return language in LOCALES ? language : DEFAULT_LOCALE;
};

const toDate = (value: string | number | Date) =>
  value instanceof Date ? value : new Date(value);

/** e.g. "Sep 22, 2026" / "22 sept 2026". */
export const formatDate = (
  value: string | number | Date | null | undefined,
  locale: AppLocale = currentLocale(),
  options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "short",
    day: "numeric",
  }
): string =>
  value == null || value === ""
    ? "—"
    : new Intl.DateTimeFormat(locale, options).format(toDate(value));

/** e.g. "September 22, 2026, 04:56 PM". */
export const formatDateTime = (
  value: string | number | Date,
  locale: AppLocale = currentLocale()
): string =>
  formatDate(value, locale, {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

/** Formats a calendar day ("2026-09-22") without shifting it across time zones. */
export const formatCalendarDay = (
  isoDay: string,
  locale: AppLocale = currentLocale()
): string =>
  new Intl.DateTimeFormat(locale, {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  }).format(new Date(`${isoDay}T00:00:00.000Z`));

/** e.g. "now", "5 min. ago", "hace 3 d". */
export const formatRelativeTime = (
  value: string | number | Date,
  locale: AppLocale = currentLocale()
): string => {
  const seconds = Math.round((toDate(value).getTime() - Date.now()) / 1000);
  const formatter = new Intl.RelativeTimeFormat(locale, {
    numeric: "auto",
    style: "narrow",
  });
  const abs = Math.abs(seconds);
  if (abs < 60) return formatter.format(0, "second");
  if (abs < 3600) return formatter.format(Math.trunc(seconds / 60), "minute");
  if (abs < 86400) return formatter.format(Math.trunc(seconds / 3600), "hour");
  return formatter.format(Math.trunc(seconds / 86400), "day");
};

const elapsedMs = (start: string, end?: string) =>
  Math.max(0, new Date(end || Date.now()).getTime() - new Date(start).getTime());

/** Compact duration such as "5h", "3d", "2mo" (translated units). */
export const formatShortDuration = (
  t: TFunction,
  start: string,
  end?: string
): string => {
  const hours = Math.floor(elapsedMs(start, end) / (60 * 60 * 1000));
  if (hours < 24) return t("common.durationShort.hours", { count: Math.max(hours, 1) });
  const days = Math.floor(hours / 24);
  if (days < 30) return t("common.durationShort.days", { count: days });
  return t("common.durationShort.months", { count: Math.floor(days / 30) });
};

/** Long duration such as "Less than a day", "3 days", "2 months", "1 year". */
export const formatLongDuration = (
  t: TFunction,
  start: string,
  end?: string
): string => {
  const days = Math.floor(elapsedMs(start, end) / (24 * 60 * 60 * 1000));
  if (days < 1) return t("common.duration.lessThanDay");
  if (days < 30) return t("common.duration.days", { count: days });
  const months = Math.floor(days / 30);
  if (months < 12) return t("common.duration.months", { count: months });
  return t("common.duration.years", { count: Math.floor(months / 12) });
};
